import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  IconButton,
  Link as MuiLink,
} from '@mui/material';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import { Link } from 'react-router-dom';
import { useListProductsQuery } from 'renderer/features/products-page/services/products.endpoints';
import { getProductStorePath } from 'renderer/features/products-page/utils/productPath';
import { getVisibleFooterDownloadLinks } from '../downloadLinks';

const Footer = () => {
  const {
    data: products,
    isLoading,
    error,
  } = useListProductsQuery({ includeHidden: false });

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#252525',
        color: 'white',
        pt: 6,
        pb: 3,
        textAlign: { xs: 'center', sm: 'left' },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* PRODUCTS */}
          {!isLoading && !error && products?.some((p) => p.showInFooter) && (
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                PRODUCTS
              </Typography>
              {products
                .filter((p) => p.showInFooter)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((product) => (
                  <Typography variant="body2" key={product._id}>
                    <Link
                      to={getProductStorePath(product)}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      {product.name}
                    </Link>
                  </Typography>
                ))}
            </Grid>
          )}

          {/* DOWNLOADS */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              DOWNLOADS
            </Typography>
            {getVisibleFooterDownloadLinks(products).map((item) => (
              <Typography variant="body2" key={item.label}>
                <a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  {item.label}
                </a>
              </Typography>
            ))}
          </Grid>

          {/* OUR COMPANY */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              OUR COMPANY
            </Typography>
            {[
              {
                label: 'Digital Anarchy',
                path: 'https://www.digitalanarchy.com',
                newWindow: true,
              },
              { label: 'License Registration', path: '/license-registration' },
              { label: 'Legal Notice', path: '/legal-notice' },
              {
                label: 'Terms and conditions of use',
                path: '/terms-and-conditions-of-use',
              },
              { label: 'About us', path: '/about-us' },
              { label: 'Secure payment', path: '/secure-payment' },
              // { label: 'Contact us', path: '/contact-us' },
            ].map((item) => (
              <Typography variant="body2" key={item.label}>
                {item.newWindow ? (
                  <a
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    to={item.path}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    {item.label}
                  </Link>
                )}
              </Typography>
            ))}
          </Grid>

          {/* STORE INFORMATION */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              STORE INFORMATION
            </Typography>
            <Typography variant="body2" color="grey.400">
              Digital Anarchy
            </Typography>
            <Typography variant="body2" color="grey.400">
              226 Tulare St.
            </Typography>
            <Typography variant="body2" color="grey.400">
              Brisbane, California 94005
            </Typography>
            <Typography variant="body2" color="grey.400">
              United States
            </Typography>
            <Typography variant="body2" color="grey.400" mt={1}>
              Call us:{' '}
              {/* <Box component="span" fontWeight="bold" color="white">
                1-415-287-6069
              </Box> */}
              <MuiLink
                href="tel:+14152876069"
                underline="hover"
                sx={{ fontWeight: 'bold', color: 'white' }}
              >
                1-415-287-6069
              </MuiLink>
            </Typography>
            <Typography variant="body2" color="grey.400">
              Email us:{' '}
              <MuiLink
                href="mailto:sales@digitalanarchy.com"
                underline="hover"
                sx={{ fontWeight: 'bold', color: 'white' }}
              >
                sales@digitalanarchy.com
              </MuiLink>
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: '#555' }} />

        <Box display="flex" justifyContent="center" gap={2} mb={2}>
          <IconButton
            size="small"
            color="inherit"
            component="a"
            href="https://x.com/Digital_Anarchy"
            target="_blank"
            rel="noopener noreferrer"
          >
            <TwitterIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="inherit"
            component="a"
            href="https://www.facebook.com/digitalanarchyfx/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FacebookIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="inherit"
            component="a"
            href="https://www.youtube.com/channel/UCJztaX9bmU1GLqyLfftGGJw"
            target="_blank"
            rel="noopener noreferrer"
          >
            <YouTubeIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="inherit"
            component="a"
            href="https://www.instagram.com/digitalanarchyplugins/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <InstagramIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography variant="body2" textAlign="center">
          Digital Anarchy LLC. All rights reserved.
          <br />
          &copy; {new Date().getFullYear()} Digital Anarchy
        </Typography>
      </Container>
    </Box>
  );
};

export default Footer;
